import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import { setCharityOrganizationCurrentPage, setCharityOrganizationPageLimit, setCharityOrganizationTotalCount, setCharityOrganizationTotalPages } from "../../../../lib/slices/pagination/charityOrganizationPageSlice";
import { setCharityOrganizationName } from "../../../../lib/slices/searchKeysSlice";
import { getAllCharityOrganizations, updateCharityOrganization } from "../../../../services/charityOrganizationApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditCharityOrganizationDialog } from "./AddEditOrganizationDialog";

// Header Data For Promotional Block Table
const headerData = [
    "createdAt",
    "order",
    "name",
    "description",
    "website",
    "status",
    "actions",
];

// Table Rows For Promotional Block Table
const tableRows = [
    "created_date",
    "order",
    "charity_organization_name",
    "charity_organization_description",
    "charity_organization_website",
    "status",
    "actions",
];

// Promotional Block Component
export function CharityOrganization() {
    // Dispatch Hook
    const dispatch = useDispatch();
    // Open Dialog Ref
    const openDialog = useRef(null);
    // Loading State
    const [isLoading, setIsLoading] = useState(true);
    // All Promotional Blocks
    const [allCharityOrganizations, setAllCharityOrganizations] = useState([]);
    // Translation Hook
    const { t } = useTranslation(["catalogue", "common"]);
    // Search Keys
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    // Get Pagination for Promotional Blocks
    const pagination = useSelector((state) => state.charityOrganizationPaginationReducer);
    // Debounced Search Term
    const debouncedSearchTerm = useDebounce(searchKeys.charityOrganizationName, 1000, true, () => {
        dispatch(setCharityOrganizationCurrentPage(1));
    });

    // Fetch All Promotional Blocks
    const handleFetchAllCharityOrganizations = async (search, page, limit,loading = true) => {
        try {
            const params = {
                search,
                page,
                limit,
            };
            setIsLoading(loading);
            const response = await getAllCharityOrganizations(params);
            if (response.data.status === "success") {
                let data = response?.data.data?.data
                const pagination = response?.data.data.pagination;
                setAllCharityOrganizations(data);
                dispatch(setCharityOrganizationPageLimit(pagination.limit));
                dispatch(setCharityOrganizationCurrentPage(pagination.page));
                dispatch(setCharityOrganizationTotalCount(pagination.total));
                dispatch(setCharityOrganizationTotalPages(pagination.totalPages));
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch All Promotional Blocks On Component Mount
    useEffect(() => {
        handleFetchAllCharityOrganizations(
            debouncedSearchTerm,
            pagination.page,
            pagination.limit,
        );
    }, [
        debouncedSearchTerm,
        pagination.page,
        pagination.limit,
    ]);

    // Handle Edit Promotional Block
    const handleEdit = (params) => {
        openDialog.current.openDialog({
            type: "edit",
            data: params.row,
            getData: handleFetchAllCharityOrganizations,
        });
    };

    // handle page change
    const handlePageChange = (event, newPage) => {
        const page = newPage + 1;
        dispatch(setCharityOrganizationCurrentPage(page));
    };

    // handle rows per page change
    const handleRowsPerPageChange = (event) => {
        console.log(event, "event");
        const limit = parseInt(event.target.value, 10);
        dispatch(setCharityOrganizationPageLimit(limit));
        dispatch(setCharityOrganizationCurrentPage(1));
    };

    // Handle Change Status Promotional Block
    const handleChangeStatus = async (event, row, key) => {
        try {
            let payload = {
                [key]: event,
            };
            const response = await updateCharityOrganization(row._id, payload);
            if (response.data.status === "success") {
                handleFetchAllCharityOrganizations(
                    searchKeys.charityOrganizationName,
                    pagination.page,
                    pagination.limit,
                    false,
                );
                handleOpenSnackBar("success", response.data.message);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        }
    };


    // Prepare Export Data For Promotional Block
    const prepareExportData = () => {
        return allCharityOrganizations?.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Name In Romanian": item.name?.ro || "-",
            "Name In English": item.name?.en || "-",
            "Description In Romanian": item.description?.ro || "-",
            "Description In English": item.description?.en || "-",
            "Status": item.status ? "Active" : "Inactive",
        }));
    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: handleFetchAllCharityOrganizations,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"charity-organizations.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={"export"}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>
            </Stack>
        );
    };

    const renderSearchAction = () => {
        return (
            <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
                <TextInput
                    styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
                    InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
                    InputEndIcon={
                        searchKeys.charityOrganizationName && (
                            <CancelOutlinedIcon
                                fontSize="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                    dispatch(
                                        setCharityOrganizationName("")
                                    );
                                }}
                            />
                        )
                    }
                    placeholder={t("searchByName", { ns: "common" })}
                    value={searchKeys.charityOrganizationName}
                    onChange={(e) => {
                        dispatch(
                            setCharityOrganizationName(e.target.value)
                        );
                    }}
                />
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allCharityOrganizations || []}
                showActions={true}
                showEdit={true}
                handleEdit={handleEdit}
                handleChangeStatus={(e, row, key) => handleChangeStatus(e, row, "status")}
                paginatedData={pagination}
                showDynamicPagination={true}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                isLoading={isLoading}
            />
        );
    }, [allCharityOrganizations, isLoading]);

    return (
        <React.Fragment>
            <PaperContainer
                title={t("charityOrganizations")}
                subtitle={t("viewCharityOrganizations")}
                headerActions={renderHeaderAction()}
                searchActions={renderSearchAction()}
            >
                {renderTable}
            </PaperContainer>

            <AddEditCharityOrganizationDialog ref={openDialog} />

        </React.Fragment>
    );
}


